import React, { useState } from 'react';
import {
    MastbevestigingUnitBasisgegevens,
    MastbevestigingUnitData,
} from '../../../../../common/src/domain/customInspectionTypes/amsterdamOverspanningen/inspectieOverspanningen';
import {
    CollapsiblePanel,
    EditPanel,
    EditPanelCell,
    EditPanelCellLabel,
    EditPanelFilterSelect,
    EditPanelInput,
    EditPanelRow,
    PanelBody,
    PanelButtonLink,
    Tray,
} from '../../../components/elements';
import { ISurveyTemplateUnitData } from '../../../components/Surveys/ISurveyTemplateUnitData';
import { EditPanelInputNumber } from '../../../components/utils/editPanel/EditPanelInputNumber';
import { useTranslate } from '../../../mixins/translate/translate';
import { BasisgegevensValue } from './components/BasisgegevensValue';
import { EditPanelStraatBagSelect } from './EditPanelStraatBagSelect';
import { EditPanelYesNoEmptyRow } from './EditPanelYesNoEmptyRow';
import { MapDialog } from './MapDialog';
import { SurveyEditPhotosPanel } from './SurveyEditPhotosPanel';
import { useInspectieOverspanningenState } from './useInspectieOverspanningenState';

export function SurveyEditAoInspectieOverspanningenUnitMastbevestiging(
    props: { surveyId: string; objectId: string; onItemChanged: () => Promise<void> } & ISurveyTemplateUnitData,
) {
    const [isMapVisible, setIsMapVisible] = useState(false);
    const translate = useTranslate();
    const {
        basisgegevensState,
        formState,
        save: saveDatabase,
        setFromState,
    } = useInspectieOverspanningenState<MastbevestigingUnitBasisgegevens, MastbevestigingUnitData>(props.surveyId, props.unitId, props.objectId);

    async function save(data: MastbevestigingUnitData) {
        await saveDatabase(data);
        await props.onItemChanged();
    }

    async function onChange(name: keyof MastbevestigingUnitData, value: any, shouldSave: boolean) {
        const newFormState = { ...formState, [name]: value };
        setFromState(newFormState);
        if (shouldSave) {
            await save(newFormState);
        }
    }
    async function onBlur() {
        if (!formState) {
            throw new Error('formState is not set');
        }
        await save(formState);
    }
    async function onMapClose(newValue?: { latitude: number; longitude: number }) {
        setIsMapVisible(false);
        if (newValue) {
            const newFormState = {
                ...formState,
                latitudeMutatie: newValue.latitude,
                longitudeMutatie: newValue.longitude,
            };
            setFromState(newFormState);
            await save(newFormState);
        }
    }

    if (formState === undefined) {
        return null;
    }

    return (
        <>
            {isMapVisible && <MapDialog onClose={onMapClose} latitude={formState.latitudeMutatie} longitude={formState.longitudeMutatie} />}
            <Tray>
                <EditPanel bottomMargin={true} noPadding={true}>
                    <CollapsiblePanel heading={{ dark: true, text: `${props.unitName} inventarisatie`, initialIsClosed: false }}>
                        <PanelBody isForm={true}>
                            <EditPanelRow>
                                <EditPanelCellLabel rowCount={2} text="Straat (BAG)"></EditPanelCellLabel>
                                <EditPanelCell rowCount={8}>
                                    <BasisgegevensValue value={basisgegevensState.straatbag} />
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelRow>
                                <EditPanelCell rowCount={2}></EditPanelCell>
                                <EditPanelCell rowCount={8}>
                                    <EditPanelStraatBagSelect
                                        onChange={(value) => void onChange('straatBagMutatie', value ?? undefined, true)}
                                        value={formState.straatBagMutatie}
                                    />
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelRow>
                                <EditPanelCellLabel rowCount={2} text="Type gedetailleerd"></EditPanelCellLabel>
                                <EditPanelCell rowCount={8}>
                                    <EditPanelFilterSelect
                                        name="typeGedetailleerdMutatie"
                                        options={[
                                            { text: 'Spanmast', value: 'Spanmast' },
                                            { text: 'GVB', value: 'GVB' },
                                        ]}
                                        value={formState.typeGedetailleerdMutatie ?? ''}
                                        handleChange={async (value) => onChange('typeGedetailleerdMutatie', value, true)}
                                    />
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelRow>
                                <EditPanelCellLabel rowCount={2} text="Coördinaten"></EditPanelCellLabel>
                                <EditPanelCell rowCount={2}>
                                    <EditPanelInputNumber
                                        name="latitudeMutatie"
                                        value={formState.latitudeMutatie}
                                        onChange={async (value) => onChange('latitudeMutatie', value, false)}
                                        onBlur={onBlur}
                                    />
                                </EditPanelCell>
                                <EditPanelCell rowCount={2}>
                                    <EditPanelInputNumber
                                        name="longitudeMutatie"
                                        value={formState.longitudeMutatie}
                                        onChange={async (value) => onChange('longitudeMutatie', value, false)}
                                        onBlur={onBlur}
                                    />
                                </EditPanelCell>
                                <EditPanelCell rowCount={4}>
                                    <PanelButtonLink
                                        text={translate('object.selectPoint')}
                                        onClick={() => setIsMapVisible(true)}
                                        small
                                        icon="fa fa-globe"
                                    ></PanelButtonLink>
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelRow>
                                <EditPanelCellLabel rowCount={2} text="Aanleghoogte"></EditPanelCellLabel>
                                <EditPanelCell rowCount={8}>
                                    <BasisgegevensValue value={basisgegevensState.aanleghoogte} unit="m" />
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelRow>
                                <EditPanelCell rowCount={2}></EditPanelCell>
                                <EditPanelCell rowCount={2}>
                                    <EditPanelInputNumber
                                        onBlur={onBlur}
                                        name="aanleghoogteMutatie"
                                        value={formState.aanleghoogteMutatie}
                                        onChange={async (value) => onChange('aanleghoogteMutatie', value, false)}
                                        unit="m"
                                    />
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelRow>
                                <EditPanelCellLabel rowCount={2} text="Opmerking"></EditPanelCellLabel>
                                <EditPanelCell rowCount={8}>
                                    <EditPanelInput
                                        name="opmerkingMutatie"
                                        value={formState.opmerkingMutatie}
                                        handleChange={async (value) => onChange('opmerkingMutatie', value, false)}
                                        handleBlur={onBlur}
                                    />
                                </EditPanelCell>
                            </EditPanelRow>
                        </PanelBody>
                    </CollapsiblePanel>

                    <CollapsiblePanel heading={{ dark: true, text: `${props.unitName} inspectie` }}>
                        <PanelBody isForm={true}>
                            <EditPanelYesNoEmptyRow
                                label="Schade aan mast?"
                                name="schadeAanMast"
                                value={formState.schadeAanMast}
                                onChange={async (value) => onChange('schadeAanMast', value, true)}
                            />
                            <EditPanelYesNoEmptyRow
                                label="Ontbrekende onderdelen aan mast?"
                                name="ontbrekendeOnderdelenAanMast"
                                value={formState.ontbrekendeOnderdelenAanMast}
                                onChange={async (value) => onChange('ontbrekendeOnderdelenAanMast', value, true)}
                            />
                            <EditPanelRow>
                                <EditPanelCellLabel
                                    rowCount={4}
                                    text="Hoek van spanmast"
                                    tooltip="Meet de scheefstand van de mast met een hoekmeter parallel aan de spandraadrichting. Noteer de scheefstand in hele graden nauwkeurig."
                                ></EditPanelCellLabel>
                                <EditPanelCell rowCount={2}>
                                    <EditPanelInputNumber
                                        onBlur={onBlur}
                                        name="hoekVanSpanmast"
                                        value={formState.hoekVanSpanmast}
                                        onChange={async (value) => onChange('hoekVanSpanmast', value, false)}
                                        unit="°"
                                    />
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelYesNoEmptyRow
                                label="Schade aan mastopzetstuk?"
                                name="schadeAanMastopzetstuk"
                                value={formState.schadeAanMastopzetstuk}
                                onChange={async (value) => onChange('schadeAanMastopzetstuk', value, true)}
                            />
                            <EditPanelYesNoEmptyRow
                                label="Ontbrekende onderdelen aan mastbeugel?"
                                name="ontbrekendeOnderdelenAanMastbeugel"
                                value={formState.ontbrekendeOnderdelenAanMastbeugel}
                                onChange={async (value) => onChange('ontbrekendeOnderdelenAanMastbeugel', value, true)}
                            />
                            <EditPanelYesNoEmptyRow
                                label="Schade aan mastbeugel?"
                                name="schadeAanMastbeugel"
                                value={formState.schadeAanMastbeugel}
                                onChange={async (value) => onChange('schadeAanMastbeugel', value, true)}
                            />

                            <EditPanelRow>
                                <EditPanelCellLabel rowCount={4} text="Opmerking"></EditPanelCellLabel>
                                <EditPanelCell rowCount={8}>
                                    <EditPanelInput
                                        handleBlur={onBlur}
                                        name="opmerking"
                                        value={formState.opmerking}
                                        handleChange={async (value) => onChange('opmerking', value, false)}
                                    />
                                </EditPanelCell>
                            </EditPanelRow>
                        </PanelBody>
                    </CollapsiblePanel>

                    <SurveyEditPhotosPanel
                        objectId={props.objectId}
                        surveyId={props.surveyId}
                        unitName={props.unitName}
                        photos={formState.fotoBestanden}
                        onPhotosChanged={async (photos) => onChange('fotoBestanden', photos, true)}
                    />
                </EditPanel>
            </Tray>
        </>
    );
}
